import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ErrorModal from '../components/ErrorModal';
import { setGlobalErrorHandler } from '../services/errorService';

const ErrorContext = createContext();

function getErrorSignature(errorData) {
  if (!errorData) return '';

  if (errorData.signature) {
    return errorData.signature;
  }

  return [
    errorData.type || 'unknown',
    errorData.title || '',
    errorData.message || '',
  ].join('::');
}

export function ErrorProvider({ children }) {
  const [error, setError] = useState(null);
  const [retryAction, setRetryAction] = useState(null);
  const errorRef = useRef(null);
  const retryActionRef = useRef(null);
  const activeSignatureRef = useRef('');

  function showError(errorData, retryFn = null) {
    const nextSignature = getErrorSignature(errorData);

    if (activeSignatureRef.current && activeSignatureRef.current === nextSignature) {
      return;
    }

    errorRef.current = errorData;
    retryActionRef.current = retryFn;
    activeSignatureRef.current = nextSignature;
    setError(errorData);
    setRetryAction(() => retryFn);
  }

  function hideError() {
    errorRef.current = null;
    retryActionRef.current = null;
    activeSignatureRef.current = '';
    setError(null);
    setRetryAction(null);
  }

  function handleRetry() {
    const callback = retryActionRef.current;

    hideError();

    if (callback) {
      window.setTimeout(() => {
        callback();
      }, 0);
    }
  }

  useEffect(() => {
    setGlobalErrorHandler(showError);
  }, []);

  return (
    <ErrorContext.Provider value={{ showError }}>
      {children}
      <ErrorModal
        error={error}
        onClose={hideError}
        onRetry={handleRetry}
        canRetry={Boolean(error?.retryable && retryAction)}
      />
    </ErrorContext.Provider>
  );
}

export function useError() {
  return useContext(ErrorContext);
}
